import { PenLine, X } from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

import { Button } from "@/components/ui/button";
import { selectOptionDisplayClassName } from "@/lib/tableBlockHelpers";
import { cn } from "@/lib/utils";

export function SelectFieldEditor({
  id,
  selected,
  options: optionsProp,
  fieldKey,
  onSelectedChange,
  onCreateOption,
  onBlur,
}: {
  id?: string;
  selected: string[];
  options: string[];
  fieldKey: string;
  onSelectedChange: (tags: string[]) => void;
  onCreateOption?: (args: {
    fieldKey: string;
    option: string;
  }) => Promise<boolean>;
  onBlur?: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<string[]>(optionsProp ?? []);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    setOptions(optionsProp ?? []);
  }, [optionsProp]);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);

  const trimmedQuery = query.trim();

  const canCreate = useMemo(() => {
    if (!trimmedQuery || isCreating || !onCreateOption) return false;
    const lower = trimmedQuery.toLowerCase();
    return !options.some((o) => o.toLowerCase() === lower);
  }, [isCreating, onCreateOption, options, trimmedQuery]);

  const closeEditing = () => {
    setIsEditing(false);
    setQuery("");
  };

  const removeTag = (tag: string) => {
    onSelectedChange(selected.filter((s) => s !== tag));
  };

  const toggleTag = (tag: string) => {
    const t = tag.trim();
    if (!t) return;
    if (selected.includes(t)) {
      removeTag(t);
    } else {
      onSelectedChange([...selected, t]);
    }
    setQuery("");
  };

  const createAndAdd = async (): Promise<boolean> => {
    if (!canCreate || !onCreateOption) return false;
    const option = trimmedQuery;
    setIsCreating(true);
    try {
      const ok = await onCreateOption({ fieldKey, option });
      if (!ok) return false;
      setOptions((prev) =>
        prev.some((o) => o.toLowerCase() === option.toLowerCase())
          ? prev
          : [...prev, option],
      );
      toggleTag(option);
      return true;
    } finally {
      setIsCreating(false);
    }
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeEditing();
      onBlur?.();
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      const lower = trimmedQuery.toLowerCase();
      const exactOption = options.find((o) => o.toLowerCase() === lower);
      if (exactOption) {
        toggleTag(exactOption);
        return;
      }
      if (canCreate) {
        void createAndAdd();
        return;
      }
      if (!trimmedQuery) return;
      if (filtered.length > 0) {
        toggleTag(filtered[0] ?? "");
      }
      return;
    }
    if (event.key === "Backspace" && query === "" && selected.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      onSelectedChange(selected.slice(0, -1));
    }
  };

  const showList = isEditing && (filtered.length > 0 || canCreate);

  return (
    <div
      className="flex items-start gap-2"
      onBlur={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        if (isEditing) closeEditing();
        onBlur?.();
      }}
    >
      {isEditing ? (
        <div
          className={cn(
            "border-input bg-background flex min-h-9 min-w-0 flex-1 flex-col rounded-md border text-sm shadow-xs",
            "focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50",
            "dark:bg-input/30 dark:border-input",
          )}
        >
          <div className="flex min-h-9 flex-wrap items-center gap-1 px-2 py-1 leading-none">
            {selected.map((tag) => (
              <span
                key={tag}
                className="inline-flex shrink-0 items-center gap-0.5 rounded-[2px] border border-[#5c5c5c] bg-[#3d3d3d] py-0 pr-0.5 pl-1 text-xs leading-none"
              >
                {tag}
                <button
                  type="button"
                  aria-label={`Remove ${tag}`}
                  className="hover:bg-background/20 rounded-[1px] p-0.5"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => removeTag(tag)}
                >
                  <X className="size-2.5" aria-hidden />
                </button>
              </span>
            ))}
            <input
              ref={inputRef}
              id={id}
              type="text"
              value={query}
              disabled={isCreating}
              placeholder={selected.length === 0 ? "Search or create…" : undefined}
              className="placeholder:text-muted-foreground min-w-[6rem] flex-1 bg-transparent px-1 py-0.5 text-sm outline-none"
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onInputKeyDown}
            />
          </div>
          {showList ? (
            <ul className="border-border max-h-[220px] overflow-y-auto border-t py-0.5">
              {canCreate ? (
                <li>
                  <button
                    type="button"
                    className="hover:bg-accent w-full px-2 py-1.5 text-left text-sm"
                    disabled={isCreating}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => void createAndAdd()}
                  >
                    Create &ldquo;{trimmedQuery}&rdquo;
                  </button>
                </li>
              ) : null}
              {filtered.map((option) => {
                const isSelected = selected.includes(option);
                return (
                  <li key={option}>
                    <button
                      type="button"
                      className={cn(
                        "hover:bg-accent w-full px-2 py-1.5 text-left text-sm",
                        isSelected && "bg-accent/50",
                      )}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => toggleTag(option)}
                    >
                      {option}
                      {isSelected ? " ✓" : ""}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : (
        <div className="flex min-h-8 min-w-0 flex-1 flex-wrap items-center gap-1">
          {selected.map((tag) => (
            <div key={tag} className={selectOptionDisplayClassName(tag)}>
              {tag}
            </div>
          ))}
        </div>
      )}
      <Button
        type="button"
        variant={isEditing ? "secondary" : "ghost"}
        size="icon-sm"
        className="mt-0.5"
        id={isEditing ? undefined : id}
        aria-label={isEditing ? "Done editing options" : "Edit options"}
        aria-pressed={isEditing}
        title={isEditing ? "Done" : "Edit"}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          if (isEditing) {
            closeEditing();
            onBlur?.();
            return;
          }
          setIsEditing(true);
        }}
      >
        <PenLine className="size-4" aria-hidden />
      </Button>
    </div>
  );
}
